import * as THREE from 'three';
import type { Environment } from './Environment';
import { damp, smoothstep } from './noise';

const GUST_PERIOD_A = 96;
const GUST_PERIOD_B = 41;

export class Wind {
  /** Normalized horizontal direction the wind blows toward (x, z). */
  readonly direction = new THREE.Vector2(1, -0.3).normalize();
  strength = 0.6;
  private readonly baseDirection = new THREE.Vector2(1, -0.3).normalize();
  private readonly guideDirection = new THREE.Vector2();
  private guideTime = 0;
  private guideDuration = 0;
  private elapsed = 0;
  private readonly offsetA = new THREE.Vector2();
  private readonly offsetB = new THREE.Vector2();
  private readonly cloudOffset = new THREE.Vector2();
  /** 0..1 gust intensity at the listener, refreshed every update. */
  gustAtPlayer = 0;
  /** 0..1 how strongly the guiding wind is active. */
  guideAmount = 0;

  constructor(private readonly env: Environment) {}

  /** Blow the whole field toward a target for a few seconds (Ghost-of-Tsushima-style guidance). */
  guide(toward: THREE.Vector2, seconds = 6): void {
    if (toward.lengthSq() < 1e-6) return;
    this.guideDirection.copy(toward).normalize();
    this.guideTime = seconds;
    this.guideDuration = seconds;
  }

  get guiding(): boolean {
    return this.guideTime > 0;
  }

  update(dt: number, listener: THREE.Vector3): void {
    this.elapsed += dt;
    const t = this.elapsed;

    // Slow meandering of the prevailing wind so the field never feels static.
    const wander = Math.sin(t * 0.021) * 0.35 + Math.sin(t * 0.0537 + 1.3) * 0.18;
    const cos = Math.cos(wander);
    const sin = Math.sin(wander);
    const bx = this.baseDirection.x * cos - this.baseDirection.y * sin;
    const bz = this.baseDirection.x * sin + this.baseDirection.y * cos;

    if (this.guideTime > 0) this.guideTime = Math.max(0, this.guideTime - dt);
    const guideTarget = this.guideTime > 0 ? smoothstep(0, 1.2, this.guideDuration - this.guideTime) * smoothstep(0, 1.8, this.guideTime) : 0;
    this.guideAmount = damp(this.guideAmount, guideTarget, 3, dt);

    const gx = bx + (this.guideDirection.x - bx) * this.guideAmount;
    const gz = bz + (this.guideDirection.y - bz) * this.guideAmount;
    const len = Math.hypot(gx, gz) || 1;
    this.direction.set(gx / len, gz / len);

    const breathing = 0.56 + Math.sin(t * 0.11) * 0.08 + Math.sin(t * 0.037 + 2.1) * 0.07;
    this.strength = breathing + this.guideAmount * 0.45;

    const gustSpeed = 5.5 + this.strength * 4 + this.guideAmount * 6;
    this.offsetA.x = (this.offsetA.x + this.direction.x * gustSpeed * dt) % GUST_PERIOD_A;
    this.offsetA.y = (this.offsetA.y + this.direction.y * gustSpeed * dt) % GUST_PERIOD_A;
    const cross = 0.6;
    this.offsetB.x = (this.offsetB.x + (this.direction.x * gustSpeed * 1.3 + this.direction.y * cross) * dt) % GUST_PERIOD_B;
    this.offsetB.y = (this.offsetB.y + (this.direction.y * gustSpeed * 1.3 - this.direction.x * cross) * dt) % GUST_PERIOD_B;
    this.cloudOffset.x -= this.direction.x * 9 * dt;
    this.cloudOffset.y -= this.direction.y * 9 * dt;

    const u = this.env.uniforms;
    u.uWindDir.value.copy(this.direction);
    u.uWindStrength.value = this.strength;
    u.uGustOffsetA.value.copy(this.offsetA);
    u.uGustOffsetB.value.copy(this.offsetB);
    u.uCloudOffset.value.copy(this.cloudOffset);

    this.gustAtPlayer = this.gustAt(listener.x, listener.z);
  }

  /** CPU mirror of GLSL gustAt(). */
  gustAt(x: number, z: number): number {
    const noise = this.env.noise;
    const a = noise.sample((x - this.offsetA.x) / GUST_PERIOD_A, (z - this.offsetA.y) / GUST_PERIOD_A, 0);
    const b = noise.sample((x - this.offsetB.x) / GUST_PERIOD_B, (z - this.offsetB.y) / GUST_PERIOD_B, 3);
    return smoothstep(0.34, 0.8, a * 0.65 + b * 0.35);
  }
}
